import { trackEvent, AnalyticsEvents } from '../analytics/instance';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { useUserStore } from '../store/userStore';
import {
  ReviewRequest,
  CreateReviewRequestData,
  UpdateReviewRequestStatusData,
  ReviewerPreview,
  ReviewStats,
} from '../types';

// Хуки для заявок на ревью
export const useReviewRequests = (
  filters?: {
    applicant?: string;
    reviewer?: string;
    status?: string;
  },
  enabled = true,
) => {
  const queryParams = new URLSearchParams();
  if (filters?.applicant) queryParams.append('applicant', filters.applicant);
  if (filters?.reviewer) queryParams.append('reviewer', filters.reviewer);
  if (filters?.status) queryParams.append('status', filters.status);

  return useQuery({
    queryKey: ['review-requests', filters],
    queryFn: async (): Promise<ReviewRequest[]> => {
      const url = `/review-requests${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      return await api(url);
    },
    placeholderData: (previousData) => previousData,
    enabled,
  });
};

export const usePendingReviewCount = () => {
  return useQuery({
    queryKey: ['review-requests', 'pending-count'],
    queryFn: async (): Promise<{ count: number }> => {
      return await api('/review-requests/pending-count');
    },
  });
};

// Превью пула ревьюеров для компетенции (кто получит заявку).
export const useReviewerPreview = (competencyId?: string, enabled = true) =>
  useQuery({
    queryKey: ['review-requests', 'reviewers', competencyId],
    queryFn: async (): Promise<ReviewerPreview> =>
      api(`/review-requests/reviewers?competencyId=${competencyId}`),
    enabled: enabled && !!competencyId,
  });

// Метрики для админского дашборда ревью.
export const useReviewStats = (enabled = true) =>
  useQuery({
    queryKey: ['review-requests', 'stats'],
    queryFn: async (): Promise<ReviewStats> => api('/review-requests/stats'),
    enabled,
  });

export const useCreateReviewRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateReviewRequestData): Promise<ReviewRequest> => {
      return await api('/review-requests', {
        method: 'POST',
        body: data,
      });
    },
    onSuccess: () => {
      trackEvent(AnalyticsEvents.COMPETENCY_REVIEW_REQUESTED);
      queryClient.invalidateQueries({ queryKey: ['review-requests'] });
      queryClient.invalidateQueries({ queryKey: ['review-requests', 'pending-count'] });
    },
  });
};

export const useUpdateReviewRequestStatus = () => {
  const queryClient = useQueryClient();
  const setUser = useUserStore((s) => s.setUser);

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateReviewRequestStatusData;
    }): Promise<ReviewRequest> => {
      return await api(`/review-requests/${id}/status`, {
        method: 'PATCH',
        body: data,
      });
    },
    onSuccess: async (_, { data }) => {
      trackEvent(AnalyticsEvents.COMPETENCY_REVIEW_DECIDED, { decision: data.status });
      queryClient.invalidateQueries({ queryKey: ['review-requests'] });
      queryClient.invalidateQueries({ queryKey: ['review-requests', 'pending-count'] });

      // Обновляем профиль пользователя, так как компетенции могли измениться
      try {
        const updatedUser = await api('/users/me');
        setUser(updatedUser);
      } catch (error) {
        console.error('Failed to refetch user profile:', error);
      }
    },
  });
};

// Ручное назначение проверяющих админом.
export const useSetReviewers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      reviewerIds,
    }: {
      id: string;
      reviewerIds: string[];
    }): Promise<ReviewRequest> => {
      return await api(`/review-requests/${id}/reviewers`, {
        method: 'PATCH',
        body: { reviewerIds },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-requests'] });
    },
  });
};

// Напомнить проверяющим о зависшей заявке.
export const useRemindReviewers = () => {
  return useMutation({
    mutationFn: async (id: string): Promise<{ reminded: number }> => {
      return await api(`/review-requests/${id}/remind`, { method: 'POST' });
    },
  });
};

export const useDeleteReviewRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      return await api(`/review-requests/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-requests'] });
      queryClient.invalidateQueries({ queryKey: ['review-requests', 'pending-count'] });
    },
  });
};
